<template>
  <div
    class="v-input-wrap"
    :class="{
      focus,
      disabled,
      error
    }"
    @click="handleFocus"
  >
    <div class="v-input">
      <!-- prepend -->
      <template v-if="isPrepend">
        <div class="v-input--prepend">
          <slot name="prepend" />
        </div>
      </template>

      <input
        ref="input"
        :class="[
          'v-input--inner',
          {
            disabled: disabled,
            password: password && !visible
          }
        ]"
        :type="inputType"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="inputListeners"
      />

      <!-- append -->
      <template v-if="isAppend">
        <span
          v-show="showAppend || !!value"
          class="v-input--append"
        >
          <slot name="append">
            <!-- show / hide -->
            <div
              v-if="eye"
              class="v-input--hide"
              @click="toggleVisible"
            >
              <IconEye
                class="icon"
                :visible="visible"
              />
            </div>
            <!-- clear -->
            <keep-alive>
              <div
                v-if="clearable && focusLazy"
                class="v-input--clear"
                :class="{
                  disabled: disabled
                }"
                @click="handleClear"
              >
                <svg
                  class="icon icon-close"
                  viewBox="0 0 1024 1024"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M551.822 512L870.4 193.422c11.378-11.378 11.378-28.444 0-39.822s-28.444-11.378-39.822 0L512 472.178 193.422 147.91c-11.378-11.378-28.444-11.378-39.822 0s-11.378 28.445 0 39.822L472.178 512 153.6 830.578c-11.378 11.378-11.378 28.444 0 39.822 5.689 5.689 11.378 5.689 22.756 5.689s17.066 0 22.755-5.689L512 551.822 830.578 870.4c5.689 5.689 11.378 5.689 22.755 5.689s17.067 0 22.756-5.689c11.378-11.378 11.378-28.444 0-39.822L551.822 512z"
                  />
                </svg>
              </div>
            </keep-alive>
          </slot>
        </span>
      </template>
      <slot />
    </div>
  </div>
</template>

<script>
import IconEye from '@ms/components/IconEye.vue'
const isIOS = () => /\(i[^;]+;( U;)? CPU.+Mac OS X/.test(navigator.userAgent)

export default {
  name: 'VInput',
  components: {
    IconEye
  },
  props: {
    type: {
      type: String,
      default: 'text'
    },
    password: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: false
    },
    // 是否可显示/隐藏
    eye: {
      type: Boolean,
      default: false
    },
    // 是否自动聚焦
    autofocus: {
      type: Boolean,
      default: false
    },
    disabled: {
      // 不可用
      type: Boolean,
      default: false
    },
    error: {
      // 错误状态
      type: Boolean,
      default: false
    },
    eyeClickable: {
      type: Boolean,
      default: true
    },
    showAppend: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: this.type !== 'password' && !this.password,
      focus: false,
      focusLazy: false,
      inputType: this.type
    }
  },
  computed: {
    isPrepend() {
      return this.$slots.prepend
    },
    isAppend() {
      return this.clearable || this.eye || this.$slots.append
    },
    showAppendText() {
      return this.visible ? 'hide' : 'show'
    },
    inputListeners: function () {
      return Object.assign({}, this.$listeners, {
        input: (e) => {
          this.$emit('input', e.target.value)
        },
        focus: (e) => {
          this.focus = true
          this.$emit('focus', e.target.value)
        },
        blur: (e) => {
          this.focus = false
          this.$emit('blur', e.target.value)
        }
      })
    }
  },
  watch: {
    focus(val) {
      this.updateFocusLazy(val)
    }
  },
  mounted() {
    if (this.autofocus) {
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    }
    this.setInputType()
  },
  methods: {
    updateFocusLazy(val) {
      this._focusTimer && clearTimeout(this._focusTimer)
      if (val) {
        this.focusLazy = true
      } else {
        this._focusTimer = setTimeout(() => {
          this.focusLazy = false
        }, 300)
      }
    },
    toggleVisible() {
      if (!this.eyeClickable) return false
      this.visible = !this.visible
      this._timer && clearTimeout(this._timer)
      this._timer = setTimeout(() => {
        this.setInputType()
      }, 300)
    },
    setInputType() {
      // password 密文模式下，希望能保持键盘类型
      if (this.password && isIOS()) {
        this.inputType = this.type
      } else {
        if (this.visible) {
          this.inputType = this.type !== 'password' ? this.type : 'text'
        } else {
          this.inputType = 'password'
        }
      }
    },
    handleClear() {
      if (this.disabled) return
      const type = 'clear'
      this.$emit('input', '', type)
      this.$emit('change', '', type)
      this.$emit('clear')
    },
    handleFocus() {
      this.$refs.input?.focus()
      // hack: 避免部分 ios 切换 password 失败
      this.$nextTick(() => {
        this.setInputType()
      })
    }
  }
}
</script>

<style lang="less">
@innerHeight: 48px;
@border-color: #ccc;

.v-input-wrap {
  overflow: hidden;
  box-sizing: border-box;
  padding: 0 20px;
  border: 1px solid @border-color;
  border-radius: 24px;
  line-height: 1;

  &.focus {
    border-color: #ffca27;
  }

  &.error {
    border-color: #FF3826;
  }
}

.v-input {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  input {
    flex: 1;
    appearance: none; /* ios按钮默认样式去掉 */
  }
}

.v-input--prepend {
  padding-right: 10px;
  border-right: 1px solid @border-color;
  line-height: 32px;
}

.v-input--inner {
  height: @innerHeight;
  box-sizing: border-box;
  padding: 0 10px;
  border: none;
  caret-color: #ffca27;
  color: #333;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: #ccc;
  }

  &.disabled {
    background: none;
    color: #999;
    cursor: not-allowed;
    opacity: 1;
    -webkit-text-fill-color: #999;
  }

  &.password {
    -webkit-text-security: disc;
  }
}

.v-input--append {
  display: inline-block;
  height: 100%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  & > div {
    display: block;
    font-style: normal;
    opacity: 0.5;
    transition: all 0.3s;

    &:hover {
      opacity: 1;
    }
  }

  .v-input--hide {
    display: flex;

    .icon {
      padding: 2px;
      color: #999;
    }
  }

  .v-input--clear {
    display: flex;
    align-items: center;
    justify-content: center;

    &.disabled {
      cursor: not-allowed;
    }

    .icon {
      width: 14px;
      height: 14px;
      fill: #999;
    }
  }
}
</style>
